import React from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Grid,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'

import { api } from '@services/api'
import type { ManualOrderBatchResponse, ManualOrderRecord, ManualOrderRequest, TokenInfo } from '../types/api'

const { Text } = Typography
const { TextArea } = Input
const { useBreakpoint } = Grid

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const cleanPayload = (values: any): ManualOrderRequest => {
  const cleaned: ManualOrderRequest = {
    symbol: String(values.symbol || '').trim().toUpperCase(),
    side: values.side,
    order_type: values.order_type,
    position_size_usdt: Number(values.position_size_usdt),
    token_ids: values.token_ids || [],
    comment: values.comment?.trim() || undefined,
  }
  if (values.order_type === 'limit') cleaned.limit_price = Number(values.limit_price)
  if (values.take_profit_price) cleaned.take_profit_price = Number(values.take_profit_price)
  if (values.stop_loss_price) cleaned.stop_loss_price = Number(values.stop_loss_price)
  return cleaned
}

const statusTag = (status: string) => {
  const normalized = String(status || '').toLowerCase()
  if (['success', 'filled'].includes(normalized)) return <Tag color="success">成交</Tag>
  if (['open', 'pending', 'partially_filled'].includes(normalized)) return <Tag color="processing">挂单中</Tag>
  if (normalized === 'tpsl_failed') return <Tag color="warning">止盈止损异常</Tag>
  if (['failed', 'rejected'].includes(normalized)) return <Tag color="error">失败</Tag>
  if (normalized === 'canceled') return <Tag>已取消</Tag>
  return <Tag>{status || '-'}</Tag>
}

const sideTag = (side: string) => {
  if (side === 'buy') return <Tag color="green">做多</Tag>
  if (side === 'sell') return <Tag color="red">做空/平多</Tag>
  return <Tag>{side}</Tag>
}

const ManualOrders: React.FC = () => {
  const [form] = Form.useForm()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const queryClient = useQueryClient()
  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(20)
  const [symbolFilter, setSymbolFilter] = React.useState<string>('')
  const orderType = Form.useWatch('order_type', form)

  const { data: tokens = [] } = useQuery({
    queryKey: ['tokens-for-manual-orders'],
    queryFn: () => api.token.list(),
  })

  const historyQuery = useQuery({
    queryKey: ['manual-orders', page, pageSize, symbolFilter],
    queryFn: () =>
      api.manualOrders.list({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        symbol: symbolFilter || undefined,
      }),
    refetchInterval: 15000,
  })

  const showBatchResult = (res: ManualOrderBatchResponse) => {
    const records = res?.records || []
    const failedRecords = records.filter((record) => record.status === 'failed' || !!record.error_message)
    const title = failedRecords.length > 0 ? `部分 Token 下单失败（${failedRecords.length}/${records.length}）` : '主动下单已提交'
    const content = (
      <div>
        {failedRecords.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="不同 Token 可能绑定不同交易所；如果该交易所没有此币种或交易所拒单，会在下面显示失败原因。"
          />
        )}
        <List
          size="small"
          dataSource={records}
          style={{ maxHeight: isMobile ? '58vh' : 420, overflowY: 'auto' }}
          renderItem={(record) => (
            <List.Item>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space wrap>
                  {statusTag(record.status || record.order_status || '')}
                  <Tag color="blue">{record.symbol}</Tag>
                  <Tag>{record.exchange?.toUpperCase() || '-'}</Tag>
                  <Text strong>{record.token_name_snapshot || `Token ${record.token_id || '-'}`}</Text>
                </Space>
                {record.error_message ? (
                  <Text type="danger">
                    <CloseCircleOutlined /> {record.error_message}
                  </Text>
                ) : (
                  <Text type="success">
                    <CheckCircleOutlined /> 订单已提交{record.order_id ? `：${record.order_id}` : ''}
                  </Text>
                )}
              </Space>
            </List.Item>
          )}
        />
      </div>
    )

    const modalConfig = {
      title,
      content,
      width: isMobile ? '94vw' : 720,
      okText: '知道了',
    }
    if (failedRecords.length > 0) {
      Modal.warning(modalConfig)
    } else {
      Modal.success(modalConfig)
    }
  }

  const createMutation = useMutation({
    mutationFn: (payload: ManualOrderRequest) => api.manualOrders.create(payload),
    onSuccess: async (res: ManualOrderBatchResponse) => {
      const failed = Number(res?.failed_count || 0)
      if (failed > 0) {
        message.warning(`提交完成，${failed} 个 Token 失败`)
      } else {
        message.success('主动下单已提交')
      }
      showBatchResult(res)
      form.resetFields()
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['manual-orders'] }),
        queryClient.invalidateQueries({ queryKey: ['orders'] }),
        queryClient.invalidateQueries({ queryKey: ['positions'] }),
      ])
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail
      message.error(detail || error?.message || '主动下单失败')
    },
  })

  const refreshMutation = useMutation({
    mutationFn: (id: number) => api.manualOrders.refresh(id),
    onSuccess: async () => {
      message.success('订单状态已刷新')
      await queryClient.invalidateQueries({ queryKey: ['manual-orders'] })
    },
  })

  const tokenOptions = (tokens || []).map((token: TokenInfo) => {
    const disabled = !token.is_active || !token.allow_trading
    const label = `${token.name} / ${(token.exchange_name || '').toUpperCase()}${disabled ? '（不可交易）' : ''}`
    return {
      label,
      value: token.id,
      disabled,
    }
  })

  const handleFinish = (values: any) => {
    const payload = cleanPayload(values)
    const selectedTokens = (tokens || []).filter((t: TokenInfo) => payload.token_ids.includes(Number(t.id)))

    Modal.confirm({
      title: '确认提交主动下单？',
      width: isMobile ? '94vw' : 640,
      content: (
        <div>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="币种">{payload.symbol}</Descriptions.Item>
            <Descriptions.Item label="方向">{payload.side === 'buy' ? '做多' : '做空/平多'}</Descriptions.Item>
            <Descriptions.Item label="类型">{payload.order_type === 'market' ? '市价' : '限价'}</Descriptions.Item>
            <Descriptions.Item label="仓位">{payload.position_size_usdt} USDT / Token</Descriptions.Item>
            {payload.limit_price && <Descriptions.Item label="限价">{payload.limit_price}</Descriptions.Item>}
            {payload.take_profit_price && <Descriptions.Item label="止盈">{payload.take_profit_price}</Descriptions.Item>}
            {payload.stop_loss_price && <Descriptions.Item label="止损">{payload.stop_loss_price}</Descriptions.Item>}
            {payload.comment && <Descriptions.Item label="备注">{payload.comment}</Descriptions.Item>}
            <Descriptions.Item label="Token">
              <Space wrap>
                {selectedTokens.map((t: TokenInfo) => (
                  <Tag key={t.id} color="blue">{t.name}</Tag>
                ))}
              </Space>
            </Descriptions.Item>
          </Descriptions>
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="提交后会对每个选中的 Token/API Key 分别发起真实交易，请确认仓位与价格。"
          />
        </div>
      ),
      okText: '确认下单',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => createMutation.mutateAsync(payload),
    })
  }

  const columns: ColumnsType<ManualOrderRecord> = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 150,
      render: (v) => dayjs(v).format('MM-DD HH:mm:ss'),
    },
    {
      title: '币种',
      dataIndex: 'symbol',
      width: 110,
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Token',
      dataIndex: 'token_name_snapshot',
      width: 130,
      ellipsis: true,
    },
    {
      title: '方向',
      dataIndex: 'side',
      width: 100,
      render: sideTag,
    },
    {
      title: '类型',
      dataIndex: 'order_type',
      width: 80,
      render: (v) => (v === 'market' ? '市价' : '限价'),
    },
    {
      title: '仓位',
      dataIndex: 'position_size_usdt',
      width: 100,
      align: 'right',
      render: (v) => `${toNumber(v).toFixed(2)}U`,
    },
    {
      title: '成交/状态',
      dataIndex: 'status',
      width: 120,
      render: (_, record) => statusTag(record.status || record.order_status || ''),
    },
    {
      title: '均价',
      dataIndex: 'avg_price',
      width: 110,
      align: 'right',
      render: (v) => (v ? toNumber(v).toFixed(6) : '-'),
    },
    {
      title: '备注',
      dataIndex: 'comment',
      ellipsis: true,
      width: 180,
      render: (v) => v || '-',
    },
    {
      title: '失败原因',
      dataIndex: 'error_message',
      ellipsis: true,
      width: 240,
      render: (v) => (v ? <Text type="danger">{v}</Text> : '-'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 90,
      render: (_, record) => (
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={refreshMutation.isPending && Number((refreshMutation.variables as any) || 0) === record.id}
          onClick={() => refreshMutation.mutate(record.id)}
        >
          刷新
        </Button>
      ),
    },
  ]

  const records: ManualOrderRecord[] = historyQuery.data?.records || []

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card
            title="主动限价/市价下单"
            extra={<Text type="secondary">仓位单位：USDT</Text>}
            bodyStyle={{ padding: isMobile ? 16 : 24 }}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{ side: 'buy', order_type: 'market' }}
              onFinish={handleFinish}
            >
              <Form.Item
                name="symbol"
                label="币种"
                rules={[{ required: true, message: '请输入交易对' }]}
              >
                <Input placeholder="如 ADAUSDT / XRPUSDT" autoCapitalize="characters" />
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="side" label="方向" rules={[{ required: true }]}>
                    <Select
                      options={[
                        { label: '做多 / Buy', value: 'buy' },
                        { label: '做空 / Sell', value: 'sell' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="order_type" label="类型" rules={[{ required: true }]}>
                    <Select
                      options={[
                        { label: '市价', value: 'market' },
                        { label: '限价', value: 'limit' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="position_size_usdt"
                label="仓位（USDT）"
                rules={[{ required: true, message: '请输入 USDT 仓位' }]}
              >
                <InputNumber min={1} precision={2} style={{ width: '100%' }} placeholder="例如 50" />
              </Form.Item>

              {orderType === 'limit' && (
                <Form.Item
                  name="limit_price"
                  label="限价价格"
                  rules={[{ required: true, message: '限价单必须输入价格' }]}
                >
                  <InputNumber min={0} precision={8} style={{ width: '100%' }} placeholder="限价价格" />
                </Form.Item>
              )}

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="take_profit_price" label="止盈价格（可空）">
                    <InputNumber min={0} precision={8} style={{ width: '100%' }} placeholder="可空" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="stop_loss_price" label="止损价格（可空）">
                    <InputNumber min={0} precision={8} style={{ width: '100%' }} placeholder="可空" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="token_ids"
                label="选择 Token / API Key"
                rules={[{ required: true, message: '请选择至少一个 Token' }]}
              >
                <Select
                  mode="multiple"
                  options={tokenOptions}
                  placeholder="可多选"
                  optionFilterProp="label"
                  maxTagCount="responsive"
                />
              </Form.Item>

              <Form.Item name="comment" label="Comment">
                <TextArea maxLength={500} showCount rows={3} placeholder="针对这一笔订单的备注" />
              </Form.Item>

              <Button
                block
                type="primary"
                danger
                size="large"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={createMutation.isPending}
              >
                确认下单
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card
            title="主动订单历史"
            extra={
              <Space>
                <Input.Search
                  allowClear
                  placeholder="筛选币种"
                  onSearch={(v) => {
                    setSymbolFilter(String(v || '').trim().toUpperCase())
                    setPage(1)
                  }}
                  style={{ width: isMobile ? 140 : 220 }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => historyQuery.refetch()}>
                  刷新
                </Button>
              </Space>
            }
          >
            {isMobile ? (
              <List
                loading={historyQuery.isLoading}
                dataSource={records}
                pagination={{
                  current: page,
                  pageSize,
                  total: historyQuery.data?.total || 0,
                  onChange: (p, ps) => {
                    setPage(p)
                    setPageSize(ps || 20)
                  },
                }}
                renderItem={(item) => (
                  <List.Item>
                    <Card size="small" style={{ width: '100%' }}>
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Space wrap>
                          <Tag color="blue">{item.symbol}</Tag>
                          {sideTag(item.side)}
                          {statusTag(item.status || item.order_status || '')}
                        </Space>
                        <Text>{item.token_name_snapshot || `Token ${item.token_id || '-'}`}</Text>
                        <Text type="secondary">
                          {item.order_type === 'market' ? '市价' : '限价'} · {toNumber(item.position_size_usdt).toFixed(2)} USDT · {dayjs(item.created_at).format('MM-DD HH:mm')}
                        </Text>
                        {item.comment && <Text>备注：{item.comment}</Text>}
                        {item.error_message && (
                          <Text type="danger">
                            <CloseCircleOutlined /> {item.error_message}
                          </Text>
                        )}
                        {!item.error_message && item.order_id && (
                          <Text type="success">
                            <CheckCircleOutlined /> {item.order_id}
                          </Text>
                        )}
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={records}
                loading={historyQuery.isLoading}
                size="small"
                scroll={{ x: 1200 }}
                pagination={{
                  current: page,
                  pageSize,
                  total: historyQuery.data?.total || 0,
                  showSizeChanger: true,
                  onChange: (p, ps) => {
                    setPage(p)
                    setPageSize(ps || 20)
                  },
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default ManualOrders
